/**
 * CR learning tree review: what happens to other rows when a person changes a
 * role, and the checks before lock (INT-221).
 *
 * Pure functions over the tree, so the review screen stays a thin view. Rules
 * (proposal "Changing a role"):
 * - Goal: rows below follow it — data items become its targets, items without
 *   data become folders (or Not imported if no data sits under them) — down to
 *   the next goal that doesn't record data or that a person set.
 * - Target / Folder: no change below or above.
 * - Not imported: the whole branch.
 * - Reset branch: back to the rule's suggestion.
 * A change never touches a row a person set, a locked row, or a row approved in
 * an open report; an option that would change one is disabled. FDM runs the
 * same checks again on lock.
 */

export type TreeRole = "GOAL" | "TARGET" | "SKIP_ITEM" | "SKIP_BRANCH";
export type RoleSource = "RULE" | "PERSON" | "FOLLOWS" | "WRITE_BACK" | "IMPORTED";

export interface ReviewNode {
  externalId: string;
  parentExternalId: string | null;
  name: string;
  dataType: string | null;
  /** What CR's UI calls the data type (e.g. Frequency); null when unknown. */
  dataTypeLabel?: string | null;
  /** Frontera's data collection type once imported (e.g. Trial); null when unknown. */
  progressType?: string | null;
  hasGoal: boolean | null;
  isBehavior: boolean | null;
  removed: boolean;
  locked: boolean;
  status: string | null; // ACTIVE | WAITING | REMOVED
  movedInCr: boolean;
  suggestedRole: TreeRole | null;
  /** The rule's role; differs from suggestedRole when it came from the import. */
  ruleRole: TreeRole | null;
  /** What CentralReach calls the item (display only); null for rows read before FDM stored it. */
  crLevel: CrLevel | null;
  /** Open reports where the item is approved: it can't change until then. */
  approvedIn: string[];
  /** Of approvedIn: reports where only its goal is approved (a target). */
  goalApprovedIn: string[];
  /** Open reports that use it (only the report being edited). */
  usedIn: string[];
  role: TreeRole | null;
  roleSource: RoleSource | null;
  confirmKind: string | null;
  confirmAnswered: boolean;
  lockedGoalExternalId: string | null;
}

export interface CheckLine {
  externalId: string;
  level: "error" | "warning";
  code: string;
  message: string;
}

export interface RoleChangeResult {
  nodes: ReviewNode[];
  /** How many rows below changed, by their new role. */
  counts: Record<TreeRole, number>;
}

const emptyCounts = (): Record<TreeRole, number> => ({ GOAL: 0, TARGET: 0, SKIP_ITEM: 0, SKIP_BRANCH: 0 });

/** What CentralReach calls an item. Worked out by the pipelines structure read and stored in FDM. */
export type CrLevel = "FOLDER" | "LTG" | "STG" | "TARGET";

/** How each role is written on the review screen. */
export const ROLE_LABEL: Record<TreeRole, string> = {
  GOAL: "Goal",
  TARGET: "Target",
  SKIP_ITEM: "Skip item",
  SKIP_BRANCH: "Skip branch",
};

export const recordsData = (node: ReviewNode): boolean => !!node.dataType;

class TreeIndex {
  readonly byId: Map<string, ReviewNode>;
  readonly kids: Map<string, ReviewNode[]>;

  constructor(nodes: ReviewNode[]) {
    this.byId = new Map(nodes.map((n) => [n.externalId, n]));
    this.kids = new Map();
    for (const node of nodes) {
      if (node.removed || !node.parentExternalId || !this.byId.has(node.parentExternalId)) continue;
      const list = this.kids.get(node.parentExternalId) ?? [];
      list.push(node);
      this.kids.set(node.parentExternalId, list);
    }
  }

  children(id: string): ReviewNode[] {
    return this.kids.get(id) ?? [];
  }

  ancestors(id: string): string[] {
    const out: string[] = [];
    const seen = new Set([id]);
    let parent = this.byId.get(id)?.parentExternalId ?? null;
    while (parent && this.byId.has(parent) && !seen.has(parent)) {
      out.push(parent);
      seen.add(parent);
      parent = this.byId.get(parent)?.parentExternalId ?? null;
    }
    return out;
  }

  descendants(id: string): string[] {
    const out: string[] = [];
    const stack = [...this.children(id)];
    const seen = new Set([id]);
    while (stack.length) {
      const node = stack.shift()!;
      if (seen.has(node.externalId)) continue;
      seen.add(node.externalId);
      out.push(node.externalId);
      stack.push(...this.children(node.externalId));
    }
    return out;
  }

  hasDataIn(id: string): boolean {
    return [id, ...this.descendants(id)].some((d) => {
      const node = this.byId.get(d);
      return !!node && !node.removed && recordsData(node);
    });
  }
}

/** Locked, or approved in an open report: nothing may change it. */
export const isFrozen = (node: ReviewNode): boolean => node.locked || node.approvedIn.length > 0;

const isProtected = (node: ReviewNode): boolean => isFrozen(node) || node.roleSource === "PERSON";

/** The goal a target belongs to: the nearest item above it set to Goal. */
export const goalOf = (nodes: ReviewNode[], id: string): string | null => {
  const index = new TreeIndex(nodes);
  const node = index.byId.get(id);
  if (node?.locked && node.lockedGoalExternalId) return node.lockedGoalExternalId;
  return index.ancestors(id).find((a) => index.byId.get(a)?.role === "GOAL") ?? null;
};

const clone = (nodes: ReviewNode[]): ReviewNode[] => nodes.map((n) => ({ ...n }));

/** A row's role under a new goal: data is a target, else skipped (alone if data is below). */
const roleUnderGoal = (index: TreeIndex, kid: ReviewNode): TreeRole => {
  if (recordsData(kid)) return "TARGET";
  return index.hasDataIn(kid.externalId) ? "SKIP_ITEM" : "SKIP_BRANCH";
};

const follow = (kid: ReviewNode, role: TreeRole, counts: Record<TreeRole, number>): void => {
  if (kid.role !== role) {
    kid.role = role;
    kid.roleSource = "FOLLOWS";
    counts[role] += 1;
  }
  answerByCascade(kid);
};

/** Goal: rows below follow it, down to the next goal; a person's rows stay. */
const cascadeGoal = (index: TreeIndex, parentId: string, counts: Record<TreeRole, number>): void => {
  for (const kid of index.children(parentId)) {
    const nextGoal = kid.role === "GOAL" && (isProtected(kid) || !recordsData(kid));
    if (nextGoal) continue;
    if (!isProtected(kid)) follow(kid, roleUnderGoal(index, kid), counts);
    cascadeGoal(index, kid.externalId, counts);
  }
};

/** Skip branch: everything below is skipped (locked and approved rows stay). */
const cascadeSkipBranch = (index: TreeIndex, id: string, counts: Record<TreeRole, number>): void => {
  for (const d of index.descendants(id)) {
    const kid = index.byId.get(d)!;
    if (!isFrozen(kid)) follow(kid, "SKIP_BRANCH", counts);
  }
};

/** Apply a person's choice on one row, cascading as described above. */
export const setRole = (nodes: ReviewNode[], id: string, role: TreeRole): RoleChangeResult => {
  const next = clone(nodes);
  const index = new TreeIndex(next);
  const counts = emptyCounts();
  const target = index.byId.get(id);
  if (!target || isFrozen(target)) return { nodes, counts };
  // CR has no goal for a folder: never a goal.
  if (role === "GOAL" && target.hasGoal === false) return { nodes, counts };

  target.role = role;
  target.roleSource = "PERSON";
  if (target.confirmKind) target.confirmAnswered = true;

  if (role === "GOAL") cascadeGoal(index, id, counts);
  else if (role === "SKIP_BRANCH") cascadeSkipBranch(index, id, counts);
  return { nodes: next, counts };
};

/**
 * A cascade answers an open Needs confirm below it. A row it didn't need to
 * change still records the answer as following the change, so the save isn't
 * read as a Reset (which would drop the answer).
 */
const answerByCascade = (kid: ReviewNode): void => {
  if (!kid.confirmKind || kid.confirmAnswered) return;
  kid.confirmAnswered = true;
  if (kid.roleSource !== "PERSON") kid.roleSource = "FOLLOWS";
};

/** Reset a row and everything under it to its suggestion (locked and approved rows stay). */
export const resetBranch = (nodes: ReviewNode[], id: string): ReviewNode[] => {
  const next = clone(nodes);
  const index = new TreeIndex(next);
  for (const d of [id, ...index.descendants(id)]) {
    const node = index.byId.get(d);
    if (!node || isFrozen(node)) continue;
    node.role = node.suggestedRole;
    // The suggestion is the import when the item was already imported.
    const imported = node.suggestedRole !== null && node.suggestedRole !== node.ruleRole && node.ruleRole !== null;
    node.roleSource = imported ? "IMPORTED" : "RULE";
    node.confirmAnswered = imported;
  }
  return next;
};

const changedBy = (
  nodes: ReviewNode[],
  change: (n: ReviewNode[]) => ReviewNode[],
  frozen: (n: ReviewNode) => boolean,
): ReviewNode[] => {
  const after = change(nodes);
  const before = new Map(nodes.map((n) => [n.externalId, n]));
  return after.filter((n) => {
    const old = before.get(n.externalId);
    if (!old || !frozen(old)) return false;
    if (old.role !== n.role) return true;
    return n.role === "TARGET" && goalOf(nodes, n.externalId) !== goalOf(after, n.externalId);
  });
};

/**
 * Why a row is Not imported, for the review screen. The rule suggests Not
 * imported only for a branch with nothing that records data (archived items
 * never reach the review), so the reason follows from who set the role.
 */
export const notImportedReason = (nodes: ReviewNode[], id: string): string | null => {
  const index = new TreeIndex(nodes);
  const node = index.byId.get(id);
  if (!node || node.removed || node.role !== "SKIP_BRANCH") return null;
  if (node.roleSource === "PERSON") {
    return recordsData(node)
      ? "You skipped this branch. It records data, which won't appear in reports."
      : "You skipped this branch.";
  }
  if (node.roleSource === "FOLLOWS") {
    const chosen = index
      .ancestors(id)
      .map((a) => index.byId.get(a))
      .find((a) => a?.role === "SKIP_BRANCH" && a.roleSource === "PERSON");
    if (chosen) return `Follows “${chosen.name}”, which you skipped.`;
  }
  return "Nothing in this branch records data in CentralReach.";
};

/** How many locked items a change would alter (role, or a target's goal). */
export const lockedChangedBy = (nodes: ReviewNode[], change: (n: ReviewNode[]) => ReviewNode[]): number =>
  changedBy(nodes, change, (n) => n.locked).length;

/** Open reports holding an approved item a change would alter. */
export const approvedChangedBy = (nodes: ReviewNode[], change: (n: ReviewNode[]) => ReviewNode[]): string[] => [
  ...new Set(changedBy(nodes, change, (n) => !n.locked && n.approvedIn.length > 0).flatMap((n) => n.approvedIn)),
];

/**
 * The options a row's dropdown can offer, with the reason one is disabled.
 * Target only for items that record data, Folder only for items that don't.
 */
export const roleOptions = (
  nodes: ReviewNode[],
  id: string,
): { role: TreeRole; disabled: boolean; reason: string | null }[] => {
  const node = nodes.find((n) => n.externalId === id);
  if (!node) return [];
  const blockedBy = (role: TreeRole): string | null => {
    if (role === node.role) return null;
    const change = (n: ReviewNode[]) => setRole(n, id, role).nodes;
    const hit = lockedChangedBy(nodes, change);
    if (hit > 0) return `Would change ${hit} locked item${hit > 1 ? "s" : ""}`;
    const reports = approvedChangedBy(nodes, change);
    if (reports.length) return `Approved in ${reports.join(", ")}`;
    return null;
  };
  const option = (role: TreeRole, cannot: string | null) => {
    if (cannot) return { role, disabled: true, reason: cannot };
    const reason = blockedBy(role);
    return { role, disabled: reason !== null, reason };
  };
  return [
    option("GOAL", node.hasGoal === false ? "A CentralReach folder can't be a goal" : null),
    option("TARGET", recordsData(node) ? null : "Doesn't record data"),
    option(
      "SKIP_ITEM",
      recordsData(node) ? "Records data, so it can't be skipped alone; use Skip branch to leave it out" : null,
    ),
    skipBranchOption(option("SKIP_BRANCH", null), nodes, id),
  ];
};

/** Skip branch, also blocked over locked items when nothing else blocks it. */
const skipBranchOption = (opt: MenuOptionShape, nodes: ReviewNode[], id: string): MenuOptionShape => {
  if (opt.disabled) return opt;
  const reason = skipBranchBlocked(nodes, id);
  return reason ? { ...opt, disabled: true, reason } : opt;
};

type MenuOptionShape = { role: TreeRole; disabled: boolean; reason: string | null };

/**
 * Skip branch over locked goals or targets would stop importing them once
 * locked, while they still show as locked here: they need an Unlock first.
 */
const skipBranchBlocked = (nodes: ReviewNode[], id: string): string | null => {
  const index = new TreeIndex(nodes);
  const locked = index
    .descendants(id)
    .map((d) => index.byId.get(d))
    .filter((n) => n && !n.removed && n.locked && (n.role === "GOAL" || n.role === "TARGET")).length;
  if (!locked) return null;
  const items = locked === 1 ? "1 locked item" : `${locked} locked items`;
  return `${items} under it: unlock them first`;
};

/**
 * The checks before lock, mirroring FDM's. ``onlyIds`` limits them to new
 * items being saved and locked after a refresh; otherwise waiting items are
 * left out (they're answered later).
 */
export const checks = (nodes: ReviewNode[], onlyIds?: Set<string>): CheckLine[] => {
  const live = nodes.filter((n) => !n.removed);
  const inScope = (n: ReviewNode) => (onlyIds ? onlyIds.has(n.externalId) : n.status !== "WAITING") && !n.locked;
  const goalsWithTargets = new Set(live.filter((n) => n.role === "TARGET").map((n) => goalOf(nodes, n.externalId)));
  return live.filter(inScope).flatMap((node) => nodeChecks(nodes, node, goalsWithTargets));
};

/** The checks the portal runs itself; FDM's lines for these are left to the local run. */
const CHECK_CODE = {
  NO_ROLE: "NO_ROLE",
  NEEDS_CONFIRM: "NEEDS_CONFIRM",
  FOLDER_AS_GOAL: "FOLDER_AS_GOAL",
  TARGET_WITHOUT_DATA: "TARGET_WITHOUT_DATA",
  TARGET_WITHOUT_GOAL: "TARGET_WITHOUT_GOAL",
  EMPTY_GOAL: "EMPTY_GOAL",
  DATA_NOT_IMPORTED: "DATA_NOT_IMPORTED",
} as const;

export const LOCAL_CHECK_CODES: ReadonlySet<string> = new Set(Object.values(CHECK_CODE));

const line = (node: ReviewNode, level: CheckLine["level"], code: string, message: string): CheckLine => ({
  externalId: node.externalId,
  level,
  code,
  message,
});

/** A target's own checks: it records data and has a goal above it. */
const targetChecks = (nodes: ReviewNode[], node: ReviewNode): CheckLine[] => [
  ...(recordsData(node) ? [] : [line(node, "error", CHECK_CODE.TARGET_WITHOUT_DATA, "A target must record data")]),
  ...(goalOf(nodes, node.externalId)
    ? []
    : [line(node, "error", CHECK_CODE.TARGET_WITHOUT_GOAL, "No goal above it. Set an item above it to Goal.")]),
];

/** Warnings that don't block the lock. */
const roleWarnings = (node: ReviewNode, goalsWithTargets: Set<string | null>): CheckLine[] => {
  if (node.role === "GOAL" && !recordsData(node) && !goalsWithTargets.has(node.externalId)) {
    return [line(node, "warning", CHECK_CODE.EMPTY_GOAL, "Empty goal: no targets and no data on the goal")];
  }
  if (node.role === "SKIP_BRANCH" && recordsData(node)) {
    return [
      line(node, "warning", CHECK_CODE.DATA_NOT_IMPORTED, "Records data but is skipped, so it won't appear in reports"),
    ];
  }
  return [];
};

const nodeChecks = (nodes: ReviewNode[], node: ReviewNode, goalsWithTargets: Set<string | null>): CheckLine[] => {
  if (!node.role) return [line(node, "error", CHECK_CODE.NO_ROLE, "Has no role yet")];
  const lines: CheckLine[] = [];
  if (node.confirmKind && !node.confirmAnswered)
    lines.push(line(node, "error", CHECK_CODE.NEEDS_CONFIRM, "Needs your answer"));
  if (node.role === "GOAL" && node.hasGoal === false) {
    lines.push(line(node, "error", CHECK_CODE.FOLDER_AS_GOAL, "A CentralReach folder can't be a goal"));
  }
  if (node.role === "TARGET") return [...lines, ...targetChecks(nodes, node)];
  return [...lines, ...roleWarnings(node, goalsWithTargets)];
};

export interface PreviewGoal {
  goal: ReviewNode;
  targets: ReviewNode[];
}

/** The right side: goals and their targets, as they'll be imported. No folders. */
export const previewGoals = (nodes: ReviewNode[]): PreviewGoal[] => {
  const live = nodes.filter((n) => !n.removed);
  const goals = live.filter((n) => n.role === "GOAL");
  return goals.map((goal) => ({
    goal,
    targets: live.filter((n) => n.role === "TARGET" && goalOf(nodes, n.externalId) === goal.externalId),
  }));
};

/** The rows a person changed since the last save, as mutation input. */
/** How a change is saved: RESET puts the row back to its suggestion in FDM. */
export type SaveSource = "PERSON" | "FOLLOWS" | "RESET";

/** Only Reset gives a row back the rule's (or the import's) source. */
const saveSourceOf = (node: ReviewNode): SaveSource => {
  if (node.roleSource === "RULE" || node.roleSource === "IMPORTED") return "RESET";
  return node.roleSource === "FOLLOWS" ? "FOLLOWS" : "PERSON";
};

/**
 * The rows that changed since the last save, as mutation input: a new role,
 * a confirm answer, or a new source (a Reset back to the suggestion, or an
 * Undo of one), even when the role itself is the same.
 */
/**
 * Fresh server data with the edits not saved yet put back (an Unlock or a
 * lock's report check refetches mid-edit). An edit is dropped when its item is
 * now locked, approved or removed.
 */
export const keepPendingEdits = (saved: ReviewNode[], local: ReviewNode[], fresh: ReviewNode[]): ReviewNode[] => {
  const pending = new Map(
    changedRoles(saved, local).map((c) => [c.externalId, local.find((n) => n.externalId === c.externalId)]),
  );
  if (!pending.size) return fresh;
  return fresh.map((n) => {
    const edit = pending.get(n.externalId);
    if (!edit || n.locked || n.removed || n.approvedIn.length) return n;
    return { ...n, role: edit.role, roleSource: edit.roleSource, confirmAnswered: edit.confirmAnswered };
  });
};

export const changedRoles = (
  original: ReviewNode[],
  current: ReviewNode[],
): { externalId: string; role: TreeRole; source: SaveSource; confirmAnswered: boolean }[] => {
  const before = new Map(original.map((n) => [n.externalId, n]));
  return current
    .filter((n) => {
      const old = before.get(n.externalId);
      return (
        !n.locked &&
        n.role &&
        old &&
        (old.role !== n.role || old.confirmAnswered !== n.confirmAnswered || old.roleSource !== n.roleSource)
      );
    })
    .map((n) => ({
      externalId: n.externalId,
      role: n.role as TreeRole,
      source: saveSourceOf(n),
      confirmAnswered: n.confirmAnswered,
    }));
};

/** The data type as CR's UI names it, else CR's code. */
export const dataTypeText = (node: Pick<ReviewNode, "dataType" | "dataTypeLabel">): string =>
  node.dataTypeLabel || node.dataType || "";

/** The data type as Frontera will have it, for the import preview; CR's name when unknown. */
export const fronteraTypeText = (node: Pick<ReviewNode, "dataType" | "dataTypeLabel" | "progressType">): string =>
  node.progressType || dataTypeText(node);
